/*
  Beyond the Fridge — Create Your Own, business configurator (snippets/btf-biz-cyo.liquid,
  rendered inside the frame in sections/btf-biz-upload.liquid on screen 27).

  The geometry, crop model, bleed maths and resolution check are lifted from the prototype
  logic in design_handoff_shopify_horizon/designs/Create Your Own - For Business.dc.html. The
  room rectangles there were measured against btf-cyo-room-biz-wall.png: the desk spans
  15–86% of the square frame and is 1200 mm wide, so 1% of the frame is 16.9 mm, and every
  board rectangle is derived from that — which is why switching size genuinely looks bigger.

  Around that sits what the shop needs: ex-VAT prices (05-business-and-po.md), the quantity,
  PO reference and proof controls from the order panel under the frame, and the same native
  cart file-upload route as assets/btf-cyo.js, including the _File missing fallback.

  Loaded as an ES module so it can import Horizon's cart event class through the importmap.
*/

// Older Safari (iOS 15) has no container units: assets/btf-cq.js sets them for the rules that use them
if (!(window.CSS && CSS.supports && CSS.supports('width: 1cqw'))) {
  import((window.btfAssets && window.btfAssets.cq) || './btf-cq.js').then((m) => m.containerUnits('.btf-bcyo__empty')).catch(() => {});
}

const CM = {
  Landscape: { Small: [55, 40], Medium: [75, 55], Large: [100, 75] },
  Portrait: { Small: [40, 55], Medium: [55, 75], Large: [75, 100] },
};
// Retail gross prices; the ex-VAT figure the business page shows is these net of VAT.
const PRICE = { Small: 85, Medium: 110, Large: 160 };
const KEY = { Landscape: 'land', Portrait: 'port' };
const SZK = { Small: 'sm', Medium: 'md', Large: 'lg' };

// Board rectangle inside each source canvas, measured from the alpha of the supplied PNGs.
const PROD = {
  Landscape: {
    ar: '1000 / 730',
    arNum: 1000 / 730,
    Large: [4.3, 2.9, 90.7, 93.2],
    Medium: [15.8, 15.6, 68.3, 68.6],
    Small: [25.0, 25.1, 49.9, 49.6],
  },
  Portrait: {
    ar: '1 / 1',
    arNum: 1,
    Large: [16.0, 4.3, 68.0, 90.6],
    Medium: [25.0, 15.8, 49.7, 68.3],
    Small: [31.6, 21.0, 36.2, 49.9],
  },
};
// The business room view: boards hang centred over the desk on btf-cyo-room-biz-wall.png.
const ROOM = {
  Landscape: { Large: [20.9, 12.9, 59.2, 44.4], Medium: [28.3, 24.8, 44.4, 32.5], Small: [34.2, 33.6, 32.5, 23.7] },
  Portrait: { Large: [28.3, 2.8, 44.4, 59.2], Medium: [34.2, 15.6, 32.5, 44.4], Small: [38.7, 24.8, 23.7, 32.5] },
};
const ORIENTS = ['Landscape', 'Portrait'];

// Quality panel grounds and rules per state: awaiting, good, fair, floor, too small.
const QUALITY = {
  empty: ['#f3f2f2', '#c9c6c3'],
  good: ['#eef4ee', '#2f6b3a'],
  fair: ['#f4f6f2', '#5b7a4a'],
  floor: ['#fdf6e3', '#a8791a'],
  bad: ['#fdeceb', '#e53712'],
};

const PREVIEW_MAX = 2400; // px on the long edge on screen; the original file is what uploads
const clamp = (v) => Math.max(-1, Math.min(1, v));
const n = (v) => Number(v).toLocaleString('en-GB');

/* --------------------------------------------------------------------------
   XHR post so the customer sees real upload progress on a 10–25 MB file.
   -------------------------------------------------------------------------- */
function postForm(url, body, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      let json = null;
      try {
        json = JSON.parse(xhr.responseText);
      } catch (e) {
        json = null;
      }
      resolve({ status: xhr.status, json, text: xhr.responseText });
    };
    xhr.onerror = () => reject(new Error('The connection dropped during the upload.'));
    xhr.ontimeout = () => reject(new Error('The upload timed out.'));
    xhr.send(body);
  });
}

/* --------------------------------------------------------------------------
   How the board reads on the order in Shopify admin. A file attached to a
   cart line shows there only as a small thumbnail, so once the line is in the
   basket its properties are rewritten in a set order, with the files as plain
   links (as the old upload app showed them) and the print details in words.
   The old key names are still read, for boards already in baskets.
   -------------------------------------------------------------------------- */
// The order line, top first: the artwork to print, the customer's original, and when they
// approved it (Duncan, 19 Oct 2026). The basket shows the artwork itself, made small by the CDN.
// Same names as assets/btf-print-jpeg.js.
const PRINT_FILE = '_Artwork (print from this)';
const APPROVED = 'Approved for print';
const ORIGINAL = '_Original file';
const OLD_ORIGINALS = ["_Customer's original (backup)", '_Original file (print from this)'];
const ORDER_KEYS = [PRINT_FILE, ORIGINAL, APPROVED, 'Screw caps', 'PO reference', 'Proof', '_Print size', '_Resolution', '_File missing', '_Original file name', '_Crop from original'];
const printJpegModule = () => import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');

function artworkUrl(props) {
  const v = props && [ORIGINAL, ...OLD_ORIGINALS, '_artwork_url'].map((k) => props[k]).find(Boolean);
  return String(v || '');
}

// A file property's full-size address, as a link that downloads the JPEG itself
function fileLink(value) {
  const urls = String(value).match(/https?:\/\/[^"',\s\]]+/g) || [];
  const full = urls.find((u) => !/_small\.[a-z]+$/i.test(u)) || urls[0];
  if (!full) return String(value);
  return /\.jpe?g$/i.test(full) ? full + '?format=pjpg' : full;
}

function readable(g, s) {
  const bx = 500 / g.wmm;
  const by = 500 / g.hmm;
  const toX = (p) => Math.round(((p - g.left) / g.dw) * s.nw);
  const toY = (p) => Math.round(((p - g.top) / g.dh) * s.nh);
  const k = [toX(0), toY(0), toX(100) - toX(0), toY(100) - toY(0)];
  const b = [toX(-bx), toY(-by), toX(100 + bx) - toX(-bx), toY(100 + by) - toY(-by)];
  return {
    size: `${s.size} ${s.orient.toLowerCase()} · ${g.wcm} × ${g.hcm} cm finished · print ${g.bwmm} × ${g.bhmm} mm (5 mm bleed each edge)`,
    crop: s.nw
      ? `From the ${n(s.nw)} × ${n(s.nh)} px original, use ${n(b[2])} × ${n(b[3])} px starting ${n(b[0])} across, ${n(b[1])} down (the board without bleed: ${n(k[2])} × ${n(k[3])} px starting ${n(k[0])}, ${n(k[1])}).`
      : '',
  };
}

// Rewrites the line just added: same values, files as links, in the order above. A failure
// leaves the line exactly as it was added, which is still complete.
async function tidyLine(item, changeUrl) {
  if (!item || !item.key || !item.properties) return;
  const props = item.properties;
  const out = {};
  for (const key of ORDER_KEYS) if (props[key] != null && props[key] !== '') out[key] = key === PRINT_FILE ? fileLink(props[key]) : String(props[key]);
  for (const key of Object.keys(props)) if (!(key in out) && props[key] != null && props[key] !== '') out[key] = String(props[key]);
  try {
    await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: out }),
    });
  } catch (e) {
    /* the line stands as added */
  }
}

function setOn(el, on) {
  if (!el) return;
  el.classList.toggle('is-on', on);
  el.setAttribute('aria-pressed', on ? 'true' : 'false');
}

class BtfBizCyo extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    const cfgEl = this.querySelector('script[data-btf-bcyo-config]');
    let cfg = {};
    try {
      cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {};
    } catch (e) {
      cfg = {};
    }
    this.cfg = cfg;
    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.refs[el.dataset.ref] = el;
    });

    // The order panel (quantity, PO reference, proof) is drawn under the frame, so it lives
    // in the section rather than in here. Read it at submit time.
    this.panel = this.closest('.btf-biz-upload') || document;

    this.state = {
      orient: ORIENTS.includes(cfg.startOrientation) ? cfg.startOrientation : 'Landscape',
      // No size ticked until the customer picks one, as on every board (Duncan, 8 Oct 2026)
      size: 'Large',
      sizeChosen: false,
      cap: 'White',
      view: 'product',
      src: null,
      nw: 0,
      nh: 0,
      zoom: 1,
      fx: 0,
      fy: 0,
      si: 0,
      dragging: false,
      srcKind: null, // 'file' | 'sample'
      srcIndex: -1,
      srcName: '',
      file: null,
      busy: false,
      progress: -1,
      status: '',
      error: '',
    };

    this.bind();
    this.render();
    this.restoreFromBasket();
  }

  // Reopened from the basket. The business tool keeps no local copy of the file, so the board is
  // rebuilt from the uploaded artwork on Shopify's CDN plus the _crop property. Quantity, PO
  // reference and proof are ordinary form fields and are restored by assets/btf-edit-line.js.
  async restoreFromBasket() {
    const editing = window.btfEditLine;
    if (!editing || !editing.active) return;
    const line = await editing.ready;
    const props = (line && line.properties) || {};
    let crop = null;
    try {
      crop = JSON.parse(props._crop || 'null');
    } catch (e) {
      crop = null;
    }
    const url = artworkUrl(props);
    if (!crop || !/^https?:/i.test(url)) return;

    const s = this.state;
    if (ORIENTS.includes(crop.orientation)) s.orient = crop.orientation;
    if (['Small', 'Medium', 'Large'].includes(crop.size)) Object.assign(s, { size: crop.size, sizeChosen: true });
    if (crop.cap === 'White' || crop.cap === 'Black') s.cap = crop.cap;
    this.render();

    this.load(
      url,
      { kind: 'file', index: -1, name: crop.file || 'artwork' },
      {
        zoom: Math.min(3, Math.max(1, Number(crop.zoom) || 1)),
        fx: clamp(Number(crop.fx) || 0),
        fy: clamp(Number(crop.fy) || 0),
      }
    );
    this.setStatus('Amend your board, then add it back to your basket.');
  }

  /* ------------------------------------------------------------- geometry */

  geom() {
    const s = this.state;
    const [wcm, hcm] = CM[s.orient][s.size];
    const room = s.view === 'room';
    const k = KEY[s.orient] + '-' + SZK[s.size];
    const cap = s.cap === 'White' ? 'white' : 'black';
    const pr = PROD[s.orient];
    const rect = room ? ROOM[s.orient][s.size] : pr[s.size];
    const has = !!s.src;

    // Crop is expressed entirely in percentages of the board box, so it is identical
    // in the product and room views regardless of how large each renders.
    const A = s.nw && s.nh ? s.nw / s.nh : 1;
    const R = wcm / hcm;
    // 5 mm bleed on every edge: the file must cover the finished size plus 10 mm overall
    // on each dimension, so the photo layer is grown past the visible plate.
    const wmm = wcm * 10;
    const hmm = hcm * 10;
    const bwmm = wmm + 10;
    const bhmm = hmm + 10;
    const dw = (A > R ? A / R : 1) * 100 * s.zoom * (bwmm / wmm);
    const dh = (A > R ? 1 : R / A) * 100 * s.zoom * (bhmm / hmm);
    const left = (100 - dw) / 2 + (s.fx * (dw - 100)) / 2;
    const top = (100 - dh) / 2 + (s.fy * (dh - 100)) / 2;

    const cropW = Math.round((s.nw * 100) / dw);
    const cropH = Math.round((s.nh * 100) / dh);
    const dpi = has ? cropW / (bwmm / 25.4) : 0;
    const reqW = Math.ceil((bwmm / 25.4) * 150);
    const reqH = Math.ceil((bhmm / 25.4) * 150);
    // Below the shop's minimum (a section setting) the board cannot go in the basket: the
    // studio can enhance a soft file, but not one this far short.
    const good = dpi >= 150;
    const fair = dpi >= 75;
    const floor = dpi >= (Number(this.cfg.minDpi) || 50);
    const tier = !has ? 'empty' : good ? 'good' : fair ? 'fair' : floor ? 'floor' : 'bad';

    return { wcm, hcm, room, k, cap, pr, rect, has, wmm, hmm, bwmm, bhmm, dw, dh, left, top, cropW, cropH, dpi, reqW, reqH, tier };
  }

  // Below the minimum: what the customer can do about it, naming a size that would work.
  tooSmall(g) {
    const s = this.state;
    const keep = s.size;
    const sizes = ['Small', 'Medium', 'Large'];
    let fits = '';
    for (const size of sizes.slice(0, sizes.indexOf(keep)).reverse()) {
      s.size = size;
      const t = this.geom().tier;
      if (t !== 'bad') {
        fits = size;
        break;
      }
    }
    s.size = keep;
    const [w, h] = CM[s.orient][fits || keep];
    return fits
      ? `Too few pixels to print well at this size. Choose the ${fits.toLowerCase()} board (${w} × ${h} cm), crop out less, or upload a larger file.`
      : 'Too few pixels to print well on any of our boards. Crop out less, or upload a larger file — the original from your camera or phone, not one sent through a messaging app.';
  }

  texts(g) {
    const qTitle =
      g.tier === 'empty'
        ? 'Awaiting your file'
        : g.tier === 'good'
          ? 'Meets production standard'
          : g.tier === 'fair'
            ? 'Under 150 dpi — we enhance it'
            : g.tier === 'floor'
              ? 'Low resolution'
              : 'Too small for this board size';

    const qBody =
      g.tier === 'empty'
        ? 'We read the pixel size the moment it lands and measure it against the print size — the finished size plus 5 mm bleed on every edge — at our production standard of 150 dpi.'
        : `${n(g.cropW)} × ${n(g.cropH)} px across the ${g.bwmm} × ${g.bhmm} mm print size — ${Math.round(g.dpi)} dpi. 150 dpi wants ${n(g.reqW)} × ${n(g.reqH)} px. ` +
          (g.tier === 'good'
            ? 'Straight to press.'
            : g.tier === 'fair'
              ? 'It will print well; we enhance it to print size before printing.'
              : g.tier === 'floor'
                ? 'Acceptable, but soft up close. Crop out less, or drop a board size.'
                : this.tooSmall(g));

    const dropNote = `JPEG, PNG or TIFF · ${Math.round((g.bwmm / 25.4) * 150)} × ${Math.round((g.bhmm / 25.4) * 150)} px for this ${g.wcm} × ${g.hcm} cm board · we check the resolution for you`;
    const cmLabel = `${g.wcm} × ${g.hcm} cm · print ${g.bwmm} × ${g.bhmm} mm`;
    const bleedNote = `Finished size ${g.wcm} × ${g.hcm} cm. Printed at ${g.bwmm} × ${g.bhmm} mm — 5 mm bleed off every edge — so the crop you see is the keep, not the whole file.`;
    return { qTitle, qBody, dropNote, cmLabel, bleedNote };
  }

  /* ------------------------------------------------------------- products */

  product(orient) {
    return (this.cfg.products && this.cfg.products[orient]) || null;
  }

  variantFor(orient, size) {
    const p = this.product(orient);
    return (p && p.variants && p.variants[size]) || null;
  }

  // Shopify keeps one inc-VAT price; the business pages show it net, as a display transform.
  exVatPence(size) {
    const v = this.variantFor(this.state.orient, size);
    const gross = v && typeof v.price === 'number' ? v.price : PRICE[size] * 100;
    return gross / (Number(this.cfg.vatRate) || 1.2);
  }

  fmtBig(p) {
    return '£ ' + (p / 100).toFixed(2);
  }

  fmtShort(p) {
    return '£' + (p / 100).toFixed(2);
  }

  /* --------------------------------------------------------------- render */

  render() {
    const s = this.state;
    const r = this.refs;
    const g = this.geom();
    const t = this.texts(g);
    const plates = this.cfg.plates || {};

    // The product plate is 1000 × 730 for landscape, square for portrait and the room.
    r.stage.style.width = `min(100%, ${Math.round(430 * (g.room ? 1 : g.pr.arNum))}px)`;
    r.stage.style.aspectRatio = g.room ? '1 / 1' : g.pr.ar;
    r.stage.classList.toggle('is-empty', !g.has);
    r.stage.classList.toggle('is-room', g.room);

    const clip = r.clip.style;
    clip.left = g.rect[0] + '%';
    clip.top = g.rect[1] + '%';
    clip.width = g.rect[2] + '%';
    clip.height = g.rect[3] + '%';
    // 35 mm corner radius on every board, so a different percentage on each size
    clip.borderRadius = (350 / g.wcm).toFixed(2) + '% / ' + (350 / g.hcm).toFixed(2) + '%';
    clip.cursor = g.has ? 'grab' : 'default';
    r.clip.tabIndex = g.has ? 0 : -1;

    const ph = r.photo.style;
    ph.left = g.left + '%';
    ph.top = g.top + '%';
    ph.width = g.dw + '%';
    ph.height = g.dh + '%';
    ph.backgroundImage = g.has ? `url("${s.src}")` : 'none';

    // Room: the photographed wall behind, and the caps drawn at the four board corners.
    // Product: the import-area plate multiplied over the photograph, and the cap plate.
    r.wall.style.backgroundImage = g.room && plates.wall ? `url("${plates.wall}")` : 'none';
    r.overlay.style.backgroundImage = g.room ? 'none' : `url("${plates['product-' + g.k + '-area'] || ''}")`;

    // Room: four cap dots drawn at the board corners, since there is no room cap plate for
    // the business wall. Product: the cap plate over the whole stage. Longhands only — the
    // inset shorthand and these longhands fight each other between the two branches.
    const caps = r.caps.style;
    if (g.room) {
      const dot = `radial-gradient(circle, ${g.cap === 'white' ? '#f4f3f1' : '#232323'} 0 40%, rgba(0,0,0,.18) 41%, transparent 62%)`;
      caps.left = g.rect[0] + '%';
      caps.top = g.rect[1] + '%';
      caps.width = g.rect[2] + '%';
      caps.height = g.rect[3] + '%';
      caps.backgroundImage = [dot, dot, dot, dot].join(', ');
      // Same geometry as the product cap plates: centres 35 mm in from each edge, the dot box
      // 20 mm across (the solid cap inside it is about 12 mm, as on the plates), all as fractions
      // of this board, so the caps keep their place against the artwork at every size. A background-position of p puts the dot's left edge at
      // p × (box − dot), so p = (centre − dot / 2) / (1 − dot).
      const sx = 20 / g.wmm;
      const sy = 20 / g.hmm;
      const px = (35 / g.wmm - sx / 2) / (1 - sx);
      const py = (35 / g.hmm - sy / 2) / (1 - sy);
      const pct = (v) => (v * 100).toFixed(2) + '%';
      caps.backgroundSize = `${pct(sx)} ${pct(sy)}`;
      caps.backgroundPosition = [
        `${pct(px)} ${pct(py)}`,
        `${pct(1 - px)} ${pct(py)}`,
        `${pct(px)} ${pct(1 - py)}`,
        `${pct(1 - px)} ${pct(1 - py)}`,
      ].join(', ');
    } else {
      caps.left = '0';
      caps.top = '0';
      caps.width = '100%';
      caps.height = '100%';
      caps.backgroundImage = `url("${plates['product-' + g.k + '-caps-' + g.cap] || ''}")`;
      caps.backgroundSize = '100% 100%';
      caps.backgroundPosition = '0 0';
    }

    r.empty.hidden = g.has;
    r.dash.classList.toggle('is-dragging', s.dragging);
    r.dropNote.textContent = t.dropNote;

    r.uploadLabel.textContent = g.has ? 'Replace photo' : 'Upload photo';
    r.cm.textContent = t.cmLabel;
    r.bleedNote.textContent = t.bleedNote;
    r.zoom.value = String(s.zoom);
    r.zoom.disabled = !g.has;
    r.fit.disabled = !g.has;

    const [qBg, qBd] = QUALITY[g.tier];
    r.quality.style.background = qBg;
    r.quality.style.borderColor = qBd;
    r.quality.dataset.tier = g.tier;
    r.qualityTitle.textContent = t.qTitle;
    r.qualityBody.textContent = s.error || t.qBody;

    setOn(r.viewProduct, !g.room);
    setOn(r.viewRoom, g.room);
    setOn(r.orientLand, s.orient === 'Landscape');
    setOn(r.orientPort, s.orient === 'Portrait');
    setOn(r.capWhite, s.cap === 'White');
    setOn(r.capBlack, s.cap === 'Black');

    this.querySelectorAll('[data-size]').forEach((b) => {
      const name = b.dataset.size;
      setOn(b, s.sizeChosen && s.size === name);
      const [w, h] = CM[s.orient][name];
      b.querySelector('[data-dims]').textContent = `${w} × ${h} cm`;
      b.querySelector('[data-price]').textContent = this.fmtShort(this.exVatPence(name));
      const v = this.variantFor(s.orient, name);
      b.classList.toggle('is-unavailable', !!(v && v.available === false));
    });

    const v = this.variantFor(s.orient, s.size);
    r.price.textContent = s.sizeChosen ? this.fmtBig(this.exVatPence(s.size)) : 'From ' + this.fmtShort(this.exVatPence('Small'));
    r.variantId.value = v ? String(v.id) : '';

    r.add.disabled = s.busy;
    r.addLabel.textContent = s.busy
      ? s.progress >= 0
        ? `${s.status} ${Math.round(s.progress * 100)}%`
        : s.status
      : this.cfg.addLabel || 'Add to business order';
    r.progress.hidden = !(s.busy && s.progress >= 0);
    r.progressBar.style.transform = `scaleX(${s.busy && s.progress >= 0 ? s.progress : 0})`;
    r.status.hidden = s.busy || !s.status;
    r.status.textContent = s.busy ? '' : s.status;
    r.error.hidden = !s.error;
    r.error.textContent = s.error;
  }

  set(patch) {
    // Any change the customer makes answers the last message, so it goes.
    if (this.state.error && !('error' in patch)) this.state.error = '';
    Object.assign(this.state, patch);
    this.render();
  }

  setStatus(text) {
    this.state.status = text;
    this.state.error = '';
    this.render();
  }

  setError(text) {
    this.state.error = text;
    this.state.status = '';
    this.render();
  }

  setProgress(p, label) {
    this.state.progress = p;
    this.state.status = label;
    this.render();
  }

  /* ----------------------------------------------------------------- bind */

  bind() {
    const r = this.refs;
    const s = this.state;

    r.upload.addEventListener('click', () => this.pickFile());
    r.sample.addEventListener('click', () => this.useSample());
    r.fileInput.addEventListener('change', () => {
      const f = r.fileInput.files && r.fileInput.files[0];
      if (f) this.loadFile(f);
      r.fileInput.value = '';
    });

    r.viewProduct.addEventListener('click', () => this.set({ view: 'product' }));
    r.viewRoom.addEventListener('click', () => this.set({ view: 'room' }));
    r.orientLand.addEventListener('click', () => this.set({ orient: 'Landscape' }));
    r.orientPort.addEventListener('click', () => this.set({ orient: 'Portrait' }));
    r.capWhite.addEventListener('click', () => this.set({ cap: 'White' }));
    r.capBlack.addEventListener('click', () => this.set({ cap: 'Black' }));
    this.querySelectorAll('[data-size]').forEach((b) => b.addEventListener('click', () => this.set({ size: b.dataset.size, sizeChosen: true })));

    r.zoom.addEventListener('input', (e) => this.set({ zoom: Number(e.target.value) }));
    r.fit.addEventListener('click', () => this.set({ zoom: 1, fx: 0, fy: 0 }));

    // The empty board is a real drop target, so the copy is honest and a dragged file
    // cannot navigate the browser away from the page.
    r.empty.addEventListener('click', () => this.pickFile());
    r.stage.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!s.dragging) this.set({ dragging: true });
    });
    r.stage.addEventListener('dragleave', (e) => {
      e.preventDefault();
      this.set({ dragging: false });
    });
    r.stage.addEventListener('drop', (e) => {
      e.preventDefault();
      this.set({ dragging: false });
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f && /^image\//.test(f.type)) this.loadFile(f);
    });

    r.clip.addEventListener('pointerdown', (e) => this.onDown(e));
    r.clip.addEventListener('keydown', (e) => this.onKey(e));
    r.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToBasket();
    });
  }

  pickFile() {
    this.refs.fileInput.click();
  }

  useSample() {
    const samples = this.cfg.samples || [];
    if (!samples.length) return;
    const i = this.state.si % samples.length;
    this.state.si += 1;
    this.state.file = null;
    this.load(samples[i], { kind: 'sample', index: i, name: '' });
  }

  loadFile(file) {
    this.state.file = file;
    this.load(URL.createObjectURL(file), { kind: 'file', index: -1, name: file.name });
  }

  load(url, meta, crop) {
    const im = new Image();
    // Artwork reopened from a basket line comes from the CDN, not a blob: URL.
    if (/^https?:/i.test(url) && url.indexOf(window.location.origin) !== 0) im.crossOrigin = 'anonymous';
    im.onload = () => {
      const nw = im.naturalWidth;
      const nh = im.naturalHeight;
      this.makePreview(im, nw, nh).then((previewUrl) => {
        const s = this.state;
        if (s.src && s.src.startsWith('blob:') && s.src !== previewUrl) URL.revokeObjectURL(s.src);
        if (url.startsWith('blob:') && url !== previewUrl) URL.revokeObjectURL(url);
        s.src = previewUrl;
        s.nw = nw;
        s.nh = nh;
        s.zoom = crop ? crop.zoom : 1;
        s.fx = crop ? crop.fx : 0;
        s.fy = crop ? crop.fy : 0;
        s.srcKind = meta.kind;
        s.srcIndex = meta.index;
        s.srcName = meta.name || '';
        s.error = '';
        s.status = '';
        this.render();
      });
    };
    im.onerror = () => {
      this.setError('That file could not be read in the browser. Upload a JPEG or PNG for the preview, or send us the file by email and we will place it for you.');
    };
    im.src = url;
  }

  // The preview is capped so a 25 MB original does not sit in memory as a full-size
  // background; the resolution check and the upload still use the original file.
  makePreview(im, nw, nh) {
    const scale = Math.min(1, PREVIEW_MAX / Math.max(nw, nh));
    if (scale === 1) return Promise.resolve(im.src);
    const c = document.createElement('canvas');
    c.width = Math.round(nw * scale);
    c.height = Math.round(nh * scale);
    c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
    return new Promise((resolve) => {
      // A board reopened from the basket loads its artwork from Shopify's CDN. If that image
      // taints the canvas, toBlob throws; the full-size image is then used as its own preview
      // rather than the load failing silently.
      try {
        c.toBlob((b) => resolve(b ? URL.createObjectURL(b) : im.src), 'image/jpeg', 0.9);
      } catch (e) {
        resolve(im.src);
      }
    });
  }

  onDown(ev) {
    const s = this.state;
    if (!s.src) return;
    const g = this.geom();
    const el = ev.currentTarget;
    const r = el.getBoundingClientRect();
    const hox = ((g.dw - 100) / 200) * r.width;
    const hoy = ((g.dh - 100) / 200) * r.height;
    const start = { x: ev.clientX, y: ev.clientY, fx: s.fx, fy: s.fy };
    el.setPointerCapture(ev.pointerId);
    ev.preventDefault();
    const move = (mv) => {
      const nfx = hox > 0.5 ? start.fx + (mv.clientX - start.x) / hox : 0;
      const nfy = hoy > 0.5 ? start.fy + (mv.clientY - start.y) / hoy : 0;
      this.set({ fx: clamp(nfx), fy: clamp(nfy) });
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }

  // Keyboard: arrows move the focal point, + and - zoom, Home refits.
  onKey(ev) {
    const s = this.state;
    if (!s.src) return;
    const step = 0.05;
    const map = {
      ArrowLeft: { fx: clamp(s.fx - step) },
      ArrowRight: { fx: clamp(s.fx + step) },
      ArrowUp: { fy: clamp(s.fy - step) },
      ArrowDown: { fy: clamp(s.fy + step) },
      '+': { zoom: Math.min(3, +(s.zoom + 0.1).toFixed(2)) },
      '=': { zoom: Math.min(3, +(s.zoom + 0.1).toFixed(2)) },
      '-': { zoom: Math.max(1, +(s.zoom - 0.1).toFixed(2)) },
      Home: { zoom: 1, fx: 0, fy: 0 },
    };
    const patch = map[ev.key];
    if (!patch) return;
    ev.preventDefault();
    this.set(patch);
  }

  /* --------------------------------------------------------------- upload */

  // The crop the customer sees, as a small JPEG for the visible "Your artwork" property.
  makeThumb(maxW) {
    const s = this.state;
    const g = this.geom();
    return new Promise((resolve) => {
      if (!s.src) return resolve(null);
      const im = new Image();
      im.onload = () => {
        try {
          const w = maxW;
          const h = Math.round(maxW / (g.wcm / g.hcm));
          const c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          const x = c.getContext('2d');
          x.fillStyle = '#fff';
          x.fillRect(0, 0, w, h);
          x.drawImage(im, (g.left / 100) * w, (g.top / 100) * h, (g.dw / 100) * w, (g.dh / 100) * h);
          c.toBlob((b) => resolve(b), 'image/jpeg', 0.82);
        } catch (e) {
          resolve(null);
        }
      };
      im.onerror = () => resolve(null);
      im.src = s.src;
    });
  }

  blobToDataUrl(blob) {
    return new Promise((resolve) => {
      if (!blob) return resolve('');
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ''));
      fr.onerror = () => resolve('');
      fr.readAsDataURL(blob);
    });
  }

  // The board plus 5 mm bleed at 150 dpi, from the full-size photo, placed exactly as on screen
  async makePrintFile(PJ, g, progress) {
    const s = this.state;
    const bx = 500 / g.wmm;
    const by = 500 / g.hmm;
    const [W, H] = PJ.printPixels(g.bwmm, g.bhmm);
    const kx = W / (100 + 2 * bx);
    const ky = H / (100 + 2 * by);
    const x = (g.left + bx) * kx;
    const y = (g.top + by) * ky;
    const w = g.dw * kx;
    const h = g.dh * ky;
    const source = s.srcKind === 'sample' ? (this.cfg.samples || [])[s.srcIndex] : s.file || s.src;
    if (!source) throw new Error('no photo');
    const bmp = await PJ.loadBitmap(source, [w, h]);
    try {
      const out = await PJ.printJpeg({
        wmm: g.bwmm,
        hmm: g.bhmm,
        progress,
        draw: (ctx, y0) => {
          ctx.translate(0, -y0);
          PJ.drawPhoto(ctx, bmp, x, y, w, h, W, H);
        },
      });
      return out.blob;
    } finally {
      if (bmp.close) bmp.close();
    }
  }

  cropJson(g) {
    const s = this.state;
    // Source-pixel boxes: the keep is the visible board, the bleed box runs 5 mm past every edge.
    const toX = (p) => Math.round(((p - g.left) / g.dw) * s.nw);
    const toY = (p) => Math.round(((p - g.top) / g.dh) * s.nh);
    const bx = 500 / g.wmm;
    const by = 500 / g.hmm;
    return JSON.stringify({
      zoom: +s.zoom.toFixed(3),
      fx: +s.fx.toFixed(4),
      fy: +s.fy.toFixed(4),
      orientation: s.orient,
      size: s.size,
      cap: s.cap,
      cm: [g.wcm, g.hcm],
      print_mm: [g.bwmm, g.bhmm],
      src_px: [s.nw, s.nh],
      keep_px: [toX(0), toY(0), toX(100) - toX(0), toY(100) - toY(0)],
      bleed_px: [toX(-bx), toY(-by), toX(100 + bx) - toX(-bx), toY(100 + by) - toY(-by)],
      dpi: Math.round(g.dpi),
      file: s.srcKind === 'sample' ? 'sample photograph' : s.srcName,
    });
  }

  // Quantity, PO reference and proof are drawn in the order panel under the frame.
  order() {
    const root = this.panel || document;
    const qtyEl = root.querySelector('[data-btf-qty]');
    const poEl = root.querySelector('[data-btf-po]');
    const proofEl = root.querySelector('[data-btf-proof]');
    const qty = Math.max(1, Math.round(Number(qtyEl && qtyEl.value) || 1));
    return {
      qty,
      po: (poEl && poEl.value ? poEl.value : '').trim(),
      proof: !!(proofEl && proofEl.checked),
    };
  }

  cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  baseForm(v, g, flag, ord) {
    const fd = new FormData();
    fd.append('id', String(v.id));
    fd.append('quantity', String(ord.qty));
    if (ord.approved) fd.append(`properties[${APPROVED}]`, ord.approved);
    const ids = this.cartSectionIds();
    if (ids.length) fd.append('sections', ids.join(','));
    fd.append('properties[_crop]', this.cropJson(g));
    // Where this board was configured, so the basket can offer to reopen it.
    fd.append('properties[_edit_url]', window.location.pathname);
    if (flag) fd.append('properties[_Resolution]', flag);
    const words = readable(g, this.state);
    fd.append('properties[_Print size]', words.size);
    if (words.crop && this.state.srcKind === 'file') fd.append('properties[_Crop from original]', words.crop);
    if (this.state.srcKind === 'file' && this.state.srcName) fd.append('properties[_Original file name]', this.state.srcName);
    if (ord.po) fd.append('properties[PO reference]', ord.po);
    fd.append('properties[Proof]', 'Proofed before printing');
    fd.append('properties[Screw caps]', this.state.cap);
    return fd;
  }

  async addToBasket() {
    const s = this.state;
    const g = this.geom();
    if (s.busy) return;
    if (!g.has) {
      this.setStatus('Choose a photograph first.');
      this.pickFile();
      return;
    }
    if (!s.sizeChosen) {
      this.setStatus('Choose a size first.');
      const first = this.querySelector('[data-size]');
      if (first) {
        first.parentElement?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        first.focus({ preventScroll: true });
      }
      return;
    }
    const v = this.variantFor(s.orient, s.size);
    if (!v) {
      this.setError('This size is not set up in the shop yet. Please contact us and we will sort it out.');
      return;
    }
    if (v.available === false) {
      this.setError('This size is not available at the moment.');
      return;
    }
    if (g.tier === 'bad') {
      this.setError(this.tooSmall(g));
      return;
    }

    const ord = this.order();
    const PJ = await printJpegModule();
    ord.approved = PJ.approval(this);
    if (ord.approved === null) return;
    s.busy = true;
    s.error = '';
    this.setProgress(-1, 'Preparing your file…');

    // Under 150 dpi the print file is enlarged to 150; the flag says how far under.
    const flag =
      g.tier === 'good'
        ? ''
        : `${g.tier === 'fair' ? 'Below 150 dpi' : 'LOW'} — the photo is ${Math.round(g.dpi)} dpi at print size, so the print file is enlarged to 150 dpi. The crop is ${n(g.cropW)} × ${n(g.cropH)} px; 150 dpi needs ${n(g.reqW)} × ${n(g.reqH)} px. Enhance before printing.`;

    // The print file: the board plus bleed at 150 dpi, cropped exactly as the customer sees it
    let printFile = null;
    let printNote = '';
    try {
      this.setProgress(0, 'Making your print file…');
      printFile = await this.makePrintFile(PJ, g, (p) => this.setProgress(p, 'Making your print file…'));
    } catch (e) {
      console.warn('[btf-biz-cyo] print file not made:', e);
      printNote = `yes — the print file could not be made in the customer's browser (${(e && e.message) || e}); make it from the original and the crop`;
    }
    const printName = `print-${s.size}-${s.orient}-${g.bwmm}x${g.bhmm}mm-150dpi.jpg`.toLowerCase();

    const maxMb = Number(this.cfg.maxMb) || 20;
    let artwork = null; // URL string or Blob
    let thumb = null; // URL string or Blob
    let pending = '';

    if (s.srcKind === 'sample') {
      artwork = (this.cfg.samples || [])[s.srcIndex] || '';
      thumb = artwork;
    } else {
      const thumbBlob = await this.makeThumb(1200);
      if (!s.file) {
        pending = 'yes — the original file was not attached (the browser session was refreshed); ask the customer for it';
        thumb = thumbBlob;
      } else if (s.file.size <= maxMb * 1048576) {
        // Shopify's own file line-item properties: the file posts with the cart line.
        artwork = s.file;
        thumb = thumbBlob;
      } else {
        pending = `yes — the file is ${(s.file.size / 1048576).toFixed(1)} MB, over the ${maxMb} MB direct-upload limit; ask the customer for the original`;
        thumb = thumbBlob;
      }
    }

    // With the artwork attached, the basket and the order show it; the small picture is only
    // for a line whose artwork could not be made
    if (printFile) thumb = null;
    const fd = this.baseForm(v, g, flag, ord);
    const fileName = s.srcName || 'artwork.jpg';
    if (printFile) fd.append(`properties[${PRINT_FILE}]`, printFile, printName);
    if (printNote) fd.append('properties[_Print file missing]', printNote);
    if (artwork instanceof Blob) fd.append(`properties[${ORIGINAL}]`, artwork, fileName);
    else if (artwork) fd.append(`properties[${ORIGINAL}]`, artwork);
    if (thumb instanceof Blob) fd.append('properties[Your artwork]', thumb, 'your-artwork.jpg');
    else if (thumb) fd.append('properties[Your artwork]', thumb);
    if (pending) fd.append('properties[_File missing]', pending);

    const usedFiles = artwork instanceof Blob || thumb instanceof Blob || !!printFile;
    this.setProgress(usedFiles ? 0 : -1, usedFiles ? 'Uploading your file…' : 'Adding to basket…');

    let res;
    try {
      res = await postForm(this.cfg.cartAddUrl, fd, usedFiles ? (p) => this.setProgress(p, 'Uploading your file…') : null);
    } catch (e) {
      res = { status: 0, json: null, error: e };
    }

    const ok = (x) => x && x.status === 200 && x.json && !x.json.status;

    // Too much to send at once: the print file and proof without the backup original.
    if (!ok(res) && printFile && artwork instanceof Blob) {
      const fd1 = this.baseForm(v, g, flag, ord);
      fd1.append(`properties[${PRINT_FILE}]`, printFile, printName);
      fd1.append('properties[_File missing]', "the customer's original was not attached (the upload was too large); the print file is complete");
      this.setProgress(0, 'Uploading your file…');
      try {
        res = await postForm(this.cfg.cartAddUrl, fd1, (p) => this.setProgress(p, 'Uploading your file…'));
      } catch (e) {
        res = { status: 0, json: null, error: e };
      }
    }

    // Last resort: keep the order moving with a small data-URL preview and flag the studio.
    if (!ok(res) && usedFiles) {
      const reason = (res.json && (res.json.description || res.json.message)) || (res.error && res.error.message) || 'HTTP ' + res.status;
      const fd2 = this.baseForm(v, g, flag, ord);
      const dataUrl = await this.blobToDataUrl(await this.makeThumb(320));
      fd2.append('properties[Your artwork]', dataUrl || 'preview unavailable');
      fd2.append('properties[_File missing]', `yes — direct upload failed (${reason}); ask the customer for the original file`);
      this.setProgress(-1, 'Adding to basket…');
      try {
        res = await postForm(this.cfg.cartAddUrl, fd2, null);
      } catch (e) {
        res = { status: 0, json: null, error: e };
      }
    }

    if (ok(res)) {
      await tidyLine(res.json, this.cfg.cartChangeUrl || '/cart/change.js');
      // Editing an existing line: the original goes only after its replacement is in, so a
      // failed add never costs the customer the board they already had.
      const editing = window.btfEditLine;
      if (editing && editing.active) await editing.replaceOriginal();
      await this.announce(res.json, v, ord.qty);
      s.busy = false;
      s.progress = -1;
      const added = ord.qty > 1 ? `${ord.qty} boards added to your basket.` : 'Added to your basket.';
      this.setStatus(editing && editing.line ? 'Your basket is updated.' : added);
      // A personalised board goes straight to the basket so the crop and size can be checked,
      // rather than leaving the customer on the tool unsure whether it worked.
      const basket = (this.cfg && this.cfg.cartUrl) || '/cart';
      window.location.assign(basket);
      return;
    } else {
      s.busy = false;
      s.progress = -1;
      const reason = (res.json && (res.json.description || res.json.message)) || (res.error && res.error.message) || '';
      this.setError(reason ? `${reason} Nothing was lost — try again.` : 'The upload did not complete. Check your connection and try again; your crop is kept.');
    }
  }

  // Tell Horizon: the header count and the cart drawer listen for this event.
  async announce(response, v, qty) {
    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      events = null;
    }
    const CartLinesUpdateEvent = events && events.CartLinesUpdateEvent;
    if (!CartLinesUpdateEvent) return;
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(v.id), quantity: qty }],
        promise: deferred.promise,
      })
    );
    try {
      const cartItems = document.querySelector('cart-items-component');
      let ajaxCart;
      if (cartItems) {
        await customElements.whenDefined('cart-items-component');
        ajaxCart = await cartItems.fetchCartData();
      } else {
        ajaxCart = await fetch(this.cfg.cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'product-form-component',
          sourceId: this.id || 'btf-biz-cyo',
          itemCount: qty,
          productId: String(v.productId || ''),
          sections: response.sections,
          didError: false,
        },
      });
    } catch (e) {
      deferred.reject(e);
    }
  }
}

if (!customElements.get('btf-biz-cyo')) customElements.define('btf-biz-cyo', BtfBizCyo);
